/**
 * DiscountRentersInsurance.com — Landing Page (React + TypeScript + React-Bootstrap)
 * Version: v01
 * Date: 2025-08-22
 *
 * What this provides
 * ------------------
 * - Sticky topbar with brand + CTA
 * - Hero section with value props and trust badges
 * - Live "Instant Estimate" panel (client-side only) that updates with coverage & discounts
 * - Lead form: name, email, phone, address, city, state, ZIP, residence type, coverage, deductible,
 *   move-in/start date, discount toggles (bundle, autopay, smoke, alarm, sprinklers, gated), notes
 * - FAQ accordion
 * - TEST_MODE toggle that logs payload instead of calling network
 * - Uses Bootstrap utilities (no hardcoded colors)
 *
 * Dependencies
 * ------------
 * npm i react-bootstrap bootstrap
 *
 * In your app entry (e.g., main.tsx or index.tsx):
 * import "bootstrap/dist/css/bootstrap.min.css";
 *
 * Usage
 * -----
 * <DiscountRentersLandingPage />
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01";
const SHOW_VERSION = true;
const TEST_MODE = true; // true => logs payload, skips network
const API_URL = "https://your-api-id.execute-api.us-east-1.amazonaws.com/prod/renters-lead"; // change me

/* ============================================================
   TYPES
============================================================ */
interface LeadPayload {
  fullName: string;
  email: string;
  phone: string; // digits only
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string; // digits only
  residenceType: "apartment" | "condo" | "townhome" | "single-family" | "room";
  coveragePersonalProperty: number; // USD
  deductible: number; // USD
  startDate: string; // YYYY-MM-DD
  discounts: {
    bundleAuto?: boolean;
    autopay?: boolean;
    smokeDetectors?: boolean;
    monitoredAlarm?: boolean;
    sprinklers?: boolean;
    gatedCommunity?: boolean;
    claimsFree?: boolean;
  };
  notes?: string;
  utm?: { source?: string; medium?: string; campaign?: string };
}

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

const digitsOnly = (s: string = "") => s.replace(/\D+/g, "");
const isValidEmail = (raw: string) => /.+@.+\..+/.test((raw || "").trim());
const isValidPhone = (raw: string) => digitsOnly(raw).length === 10;
const isValidZip = (raw: string) => /^(\d{5})(?:-\d{4})?$/.test((raw || "").trim());
const todayDateOnly = () => new Date().toISOString().slice(0, 10);

/* Simple client-side estimator: not a quote. */
function estimateMonthly(coverage: number, deductible: number, d: LeadPayload["discounts"], stateCode: string) {
  // Base derived from coverage; state factor is simplistic placeholder.
  const basePer10k = 2.2; // $ per $10k personal property
  const stateFactor = ["CA","FL","NY","LA","TX"].includes(stateCode) ? 1.25 : 1.0;
  let est = Math.max(8, (coverage / 10000) * basePer10k * stateFactor);

  // Deductible effect: higher deductible lowers cost
  est *= deductible >= 1000 ? 0.9 : 1.0;

  // Discounts stack multiplicatively with caps
  let disc = 1.0;
  if (d.bundleAuto) disc *= 0.9;
  if (d.autopay) disc *= 0.97;
  if (d.smokeDetectors) disc *= 0.98;
  if (d.monitoredAlarm) disc *= 0.95;
  if (d.sprinklers) disc *= 0.98;
  if (d.gatedCommunity) disc *= 0.985;
  if (d.claimsFree) disc *= 0.95;

  est *= disc;
  return Math.max(7, Number(est.toFixed(2)));
}

/* ============================================================
   SMALL UI BITS
============================================================ */
const Feature: React.FC<{ title: string; text: string; emoji: string }> = ({ title, text, emoji }) => (
  <Card className="h-100 shadow-sm">
    <Card.Body>
      <div className="display-6" aria-hidden>{emoji}</div>
      <h5 className="mt-3 mb-2">{title}</h5>
      <p className="mb-0 text-body-secondary">{text}</p>
    </Card.Body>
  </Card>
);

const Step: React.FC<{ n: number; title: string; text: string }> = ({ n, title, text }) => (
  <div className="d-flex align-items-start gap-3">
    <div className="badge rounded-pill text-bg-primary fs-6 mt-1">{n}</div>
    <div>
      <h6 className="mb-1">{title}</h6>
      <p className="mb-0 text-body-secondary">{text}</p>
    </div>
  </div>
);

/* ============================================================
   MAIN COMPONENT
============================================================ */
export const RentersInsuranceLandingPage_v01: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);

  // Lead fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("CO");
  const [zip, setZip] = useState("");
  const [residenceType, setResidenceType] = useState<LeadPayload["residenceType"]>("apartment");
  const [coverage, setCoverage] = useState<number>(30000);
  const [deductible, setDeductible] = useState<number>(500);
  const [startDate, setStartDate] = useState<string>("");
  const [discounts, setDiscounts] = useState<LeadPayload["discounts"]>({
    bundleAuto: true,
    autopay: true,
    smokeDetectors: true,
    monitoredAlarm: false,
    sprinklers: false,
    gatedCommunity: false,
    claimsFree: true,
  });
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);

  const quoteEstimate = useMemo(
    () => estimateMonthly(coverage, deductible, discounts, state),
    [coverage, deductible, discounts, state]
  );

  const valid = useMemo(() => {
    return (
      fullName.trim().length >= 2 &&
      isValidEmail(email) &&
      isValidPhone(phone) &&
      address1.trim().length > 3 &&
      city.trim().length > 1 &&
      Boolean(state) &&
      isValidZip(zip) &&
      Number.isFinite(coverage) && coverage >= 10000 && coverage <= 200000 &&
      (deductible === 250 || deductible === 500 || deductible === 1000) &&
      /^\d{4}-\d{2}-\d{2}$/.test(startDate)
    );
  }, [fullName, email, phone, address1, city, state, zip, coverage, deductible, startDate]);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitOk(false);

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    const payload: LeadPayload = {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),
      address1: address1.trim(),
      address2: address2.trim() || undefined,
      city: city.trim(),
      state,
      zip: zip.trim(),
      residenceType,
      coveragePersonalProperty: Number(coverage),
      deductible: Number(deductible),
      startDate,
      discounts,
      notes: notes.trim() || undefined,
      utm: {
        source: new URLSearchParams(window.location.search).get("utm_source") || undefined,
        medium: new URLSearchParams(window.location.search).get("utm_medium") || undefined,
        campaign: new URLSearchParams(window.location.search).get("utm_campaign") || undefined,
      },
    };

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        // eslint-disable-next-line no-console
        console.log("[TEST_MODE] Submitting payload:", payload);
        await new Promise((r) => setTimeout(r, 800));
        setSubmitOk(true);
        return;
      }

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        mode: "cors",
      });

      if (!res.ok) throw new Error(await res.text());

      setSubmitOk(true);
      // reset selective fields
      setFullName(""); setEmail(""); setPhone("");
      setAddress1(""); setAddress2(""); setCity(""); setState("CO"); setZip("");
      setResidenceType("apartment"); setCoverage(30000); setDeductible(500); setStartDate("");
      setDiscounts({ bundleAuto: true, autopay: true, smokeDetectors: true, monitoredAlarm: false, sprinklers: false, gatedCommunity: false, claimsFree: true });
      setNotes("");
    } catch (err: any) {
      setSubmitError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Discount<span className="text-primary">Renters</span>Insurance.com
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Talk to an agent:</div>
              <Button variant="outline-primary" href="tel:3033375434">(303) 337‑5434</Button>
              <Button variant="primary" onClick={scrollToForm} className="ms-md-2">Get a Quote</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero */}
      <section className="bg-primary bg-gradient text-white py-5">
        <Container>
          <Row className="align-items-center g-4">
            <Col lg={6}>
              <h1 className="display-5 fw-bold">Renters Insurance, Big Discounts</h1>
              <p className="lead">Protect your stuff for less. Claims‑free, bundle, and smart‑home discounts available in many states.</p>
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <Badge bg="light" text="dark">Bundle & Save</Badge>
                <Badge bg="light" text="dark">Claims‑Free</Badge>
                <Badge bg="light" text="dark">AutoPay</Badge>
                <Badge bg="light" text="dark">Smart‑Home</Badge>
              </div>
              <div className="mt-4 d-flex gap-2">
                <Button variant="light" onClick={scrollToForm}>Start My Free Quote</Button>
                <Button variant="outline-light" href="#faq">Common Questions</Button>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg">
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Instant Estimate</div>
                      <small className="text-body-secondary">For illustration only — not a quote</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-end">
                    <div>
                      <div className="text-body-secondary">Estimated from your selections</div>
                      <div className="display-6 fw-bold">${quoteEstimate}/mo</div>
                    </div>
                    <div className="text-end small text-body-secondary">
                      <div>Coverage: ${coverage.toLocaleString()}</div>
                      <div>Deductible: ${deductible.toLocaleString()}</div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why Choose Us */}
      <section className="py-5">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Why DiscountRentersInsurance.com</h2>
              <p className="text-body-secondary mb-0">We compare carriers and apply every eligible discount we can find.</p>
            </Col>
          </Row>
          <Row xs={1} md={3} className="g-3">
            <Col><Feature emoji="💸" title="Real Savings" text="Bundle, claims‑free, and smart‑home discounts — it adds up." /></Col>
            <Col><Feature emoji="⚡" title="Fast Coverage" text="Get proof of insurance quickly, often same‑day." /></Col>
            <Col><Feature emoji="🧠" title="Helpful Humans" text="Licensed advisors explain options in plain language." /></Col>
          </Row>
        </Container>
      </section>

      {/* Form + Discounts */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="g-4" ref={formRef}>
            <Col lg={7}>
              <Card className="shadow-sm">
                <Card.Header className="bg-white">
                  <div className="fw-semibold">Fast, No‑Obligation Quote</div>
                  <small className="text-body-secondary">Takes about 90 seconds</small>
                </Card.Header>
                <Card.Body>
                  {submitOk && (
                    <Alert variant="success" onClose={() => setSubmitOk(false)} dismissible>
                      Thanks! An agent will reach out shortly with your options.
                    </Alert>
                  )}
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      {submitError}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit} noValidate>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Label htmlFor="fullName">Full Name</Form.Label>
                        <Form.Control id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required isInvalid={fullName !== "" && fullName.trim().length < 2} />
                        <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor="email">Email</Form.Label>
                        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required isInvalid={email !== "" && !isValidEmail(email)} />
                        <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                      </Col>

                      <Col md={6}>
                        <Form.Label htmlFor="phone">Phone</Form.Label>
                        <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123‑4567" required isInvalid={phone !== "" && !isValidPhone(phone)} />
                        <Form.Control.Feedback type="invalid">Enter a 10‑digit phone.</Form.Control.Feedback>
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor="residenceType">Residence Type</Form.Label>
                        <Form.Select id="residenceType" value={residenceType} onChange={(e) => setResidenceType(e.target.value as LeadPayload["residenceType"])}>
                          <option value="apartment">Apartment</option>
                          <option value="condo">Condo</option>
                          <option value="townhome">Townhome</option>
                          <option value="single-family">Single‑Family</option>
                          <option value="room">Room</option>
                        </Form.Select>
                      </Col>

                      <Col md={8}>
                        <Form.Label htmlFor="address1">Address</Form.Label>
                        <Form.Control id="address1" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required isInvalid={address1 !== "" && address1.trim().length <= 3} />
                        <Form.Control.Feedback type="invalid">Enter a valid street address.</Form.Control.Feedback>
                      </Col>
                      <Col md={4}>
                        <Form.Label htmlFor="address2">Apt/Suite (optional)</Form.Label>
                        <Form.Control id="address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Apt 4B" />
                      </Col>

                      <Col md={5}>
                        <Form.Label htmlFor="city">City</Form.Label>
                        <Form.Control id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Denver" required isInvalid={city !== "" && city.trim().length <= 1} />
                        <Form.Control.Feedback type="invalid">Enter your city.</Form.Control.Feedback>
                      </Col>
                      <Col md={3}>
                        <Form.Label htmlFor="state">State</Form.Label>
                        <Form.Select id="state" value={state} onChange={(e) => setState(e.target.value)} required>
                          {US_STATES.map((s) => (
                            <option key={s.code} value={s.code}>{s.name}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={4}>
                        <Form.Label htmlFor="zip">ZIP</Form.Label>
                        <Form.Control id="zip" inputMode="numeric" value={zip} onChange={(e) => setZip(e.target.value)} placeholder="80202" required isInvalid={zip !== "" && !isValidZip(zip)} />
                        <Form.Control.Feedback type="invalid">Enter a valid ZIP code.</Form.Control.Feedback>
                      </Col>

                      <Col md={6}>
                        <Form.Label htmlFor="coverage">Personal Property Coverage</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Range id="coverage" min={10000} max={200000} step={5000} value={coverage} onChange={(e) => setCoverage(Number(e.target.value))} />
                          <div className="text-nowrap small">${coverage.toLocaleString()}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor="deductible">Deductible</Form.Label>
                        <Form.Select id="deductible" value={deductible} onChange={(e) => setDeductible(Number(e.target.value))}>
                          <option value={250}>$250</option>
                          <option value={500}>$500</option>
                          <option value={1000}>$1,000</option>
                        </Form.Select>
                      </Col>

                      <Col md={6}>
                        <Form.Label htmlFor="startDate">Desired Start Date</Form.Label>
                        <Form.Control id="startDate" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} min={todayDateOnly()} required />
                      </Col>

                      <Col md={12}>
                        <Form.Label>Discounts & Safety</Form.Label>
                        <div className="d-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.5rem" }}>
                          <Form.Check type="switch" id="bundleAuto" label="Bundle with Auto" checked={!!discounts.bundleAuto} onChange={(e) => setDiscounts({ ...discounts, bundleAuto: e.currentTarget.checked })} />
                          <Form.Check type="switch" id="autopay" label="AutoPay" checked={!!discounts.autopay} onChange={(e) => setDiscounts({ ...discounts, autopay: e.currentTarget.checked })} />
                          <Form.Check type="switch" id="smokeDetectors" label="Smoke Detectors" checked={!!discounts.smokeDetectors} onChange={(e) => setDiscounts({ ...discounts, smokeDetectors: e.currentTarget.checked })} />
                          <Form.Check type="switch" id="monitoredAlarm" label="Monitored Alarm" checked={!!discounts.monitoredAlarm} onChange={(e) => setDiscounts({ ...discounts, monitoredAlarm: e.currentTarget.checked })} />
                          <Form.Check type="switch" id="sprinklers" label="Fire Sprinklers" checked={!!discounts.sprinklers} onChange={(e) => setDiscounts({ ...discounts, sprinklers: e.currentTarget.checked })} />
                          <Form.Check type="switch" id="gatedCommunity" label="Gated Community" checked={!!discounts.gatedCommunity} onChange={(e) => setDiscounts({ ...discounts, gatedCommunity: e.currentTarget.checked })} />
                          <Form.Check type="switch" id="claimsFree" label="Claims‑Free (3 yrs)" checked={!!discounts.claimsFree} onChange={(e) => setDiscounts({ ...discounts, claimsFree: e.currentTarget.checked })} />
                        </div>
                      </Col>

                      <Col md={12}>
                        <Form.Label htmlFor="notes">Notes (optional)</Form.Label>
                        <Form.Control id="notes" as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else we should know? Pets, roommates, high‑value items, etc." />
                      </Col>
                    </Row>

                    <div className="d-grid mt-3">
                      <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                        {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                      </Button>
                      <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                        By clicking, you agree we may contact you about insurance options. No spam. You can opt out anytime.
                      </div>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="shadow-sm h-100">
                <Card.Header className="bg-white fw-semibold">Popular Discounts We Check</Card.Header>
                <Card.Body>
                  <ul className="mb-3">
                    <li>Bundle with auto policy</li>
                    <li>Claims‑free history</li>
                    <li>AutoPay and paperless</li>
                    <li>Smoke detectors, monitored alarm, sprinklers</li>
                    <li>Gated community or secured building</li>
                    <li>Higher deductible options</li>
                  </ul>
                  <div className="small text-body-secondary">Discounts vary by state and carrier. Eligibility not guaranteed.</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to get covered.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}><Step n={1} title="Tell us about you" text="Share basic info and your address to match local rates." /></Col>
            <Col md={4}><Step n={2} title="We apply discounts" text="We check bundles, safety devices, and claims‑free savings." /></Col>
            <Col md={4}><Step n={3} title="Choose & bind" text="Pick a plan and start coverage. Proof often available immediately." /></Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
              <p className="text-body-secondary mb-0">Short answers to common questions.</p>
            </Col>
          </Row>

          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>What does renters insurance cover?</Accordion.Header>
              <Accordion.Body>
                Typically personal property, personal liability, and additional living expenses (loss of use) after a covered claim. Exact coverage varies by policy.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>How can I lower my rate?</Accordion.Header>
              <Accordion.Body>
                Bundle with auto, choose a higher deductible you can afford, use AutoPay/paperless, and add safety devices like smoke detectors and monitored alarms.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>Is my landlord’s policy enough?</Accordion.Header>
              <Accordion.Body>
                Landlord insurance covers the building, not your belongings or your liability. Renters insurance protects your stuff and your liability.
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} DiscountRentersInsurance.com. All rights reserved.
              <div className="mt-2">Insurance availability and discounts vary by state and carrier. This page is for general information and not legal advice.</div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="outline-secondary" size="sm" onClick={scrollToForm}>Get a Quote</Button>
              <Button variant="secondary" size="sm" href="tel:3033375434">Call (303) 337‑5434</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default RentersInsuranceLandingPage_v01;
